import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import UserNavbar from "../UserNavbar";
import UserFooter from "../UserFooter";
import MainSidebar from "../Mainsidebar";
import "../../Style/SendBulkEmail.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faMagicWandSparkles, 
  faWandMagicSparkles,
  faPaperPlane, 
  faUsers, 
  faFileCsv,
  faHistory,
  faCheckCircle,
  faSpinner
} from "@fortawesome/free-solid-svg-icons";
import { emailAIService } from "../../Services/emailAIService";
import EmailDraftHistory from "../components/EmailDraftHistory";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const emailPurposes = [
  "Product Promotion", "Marketing Campaign", "Customer Follow-up", 
  "Lead Nurturing", "Vendor Communication", "Meeting Invitation", 
  "Event Announcement", "Offer & Discount Campaign", 
  "Payment Reminder", "Customer Support Update"
];

const placeholders = [
  "{{CustomerName}}", "{{CompanyName}}", "{{VendorName}}", 
  "{{Designation}}", "{{City}}", "{{ProductName}}"
];

export default function SendBulkEmail() {
  const [hamburger, sethamburger] = useState(true);
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const handleclickonbutton = (hamburger) => {
    if (document.documentElement.clientWidth <= 800)
      return { width: "100%", marginLeft: "0rem" };
    else
      return {
        width: !hamburger ? "-webkit-fill-available" : undefined,
        marginLeft: hamburger ? "15.5rem" : "4.5rem",
        transition: "margin-left 0.3s ease-in-out"
      };
  };

  // Form State
  const [aiConfig, setAiConfig] = useState({
    purpose: "",
    audience: "",
    productName: "",
    tone: "Professional",
    keywords: "",
    instructions: ""
  });

  const [draft, setDraft] = useState({
    subject: "",
    greeting: "",
    body: "",
    cta: ""
  });

  const [recipients, setRecipients] = useState([]);
  const [recipientTab, setRecipientTab] = useState("crm"); // 'crm' or 'csv'

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setAiConfig(prev => ({ ...prev, [name]: value }));
  };

  const handleDraftChange = (e) => {
    const { name, value } = e.target;
    setDraft(prev => ({ ...prev, [name]: value }));
  };

  const saveDraftToHistory = (draftToSave) => {
    const savedDrafts = JSON.parse(localStorage.getItem("aiEmailDrafts") || "[]");
    const newDraft = {
      ...draftToSave,
      purpose: aiConfig.purpose || "Custom",
      timestamp: new Date().toISOString()
    };
    const updatedDrafts = [newDraft, ...savedDrafts].slice(0, 20); // Keep last 20
    localStorage.setItem("aiEmailDrafts", JSON.stringify(updatedDrafts));
  };

  const handleGenerateAI = async () => {
    if (!aiConfig.purpose || !aiConfig.productName) {
      toast.error("Purpose and Product Name are required.");
      return;
    }

    setIsLoading(true);
    try {
      const generatedDraft = await emailAIService.generateEmailDraft(aiConfig);
      setDraft({
        subject: generatedDraft.subject || "",
        greeting: generatedDraft.greeting || "",
        body: generatedDraft.body || "",
        cta: generatedDraft.cta || ""
      });
      saveDraftToHistory(generatedDraft);
      setStep(2);
      toast.success("Draft generated successfully!");
    } catch (error) {
      toast.error("Failed to generate draft. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const applySuggestion = async (action) => {
    setIsLoading(true);
    try {
      const improved = await emailAIService.improveEmail(draft.body, action);
      setDraft(prev => ({ ...prev, body: improved.content }));
      toast.success("Applied AI suggestion.");
    } catch (error) {
      toast.error("Failed to apply suggestion.");
    } finally {
      setIsLoading(false);
    }
  };

  const insertPlaceholder = (tag) => {
    const value = window.prompt(`Please enter the value for ${tag}:`);
    if (value !== null && value.trim() !== "") {
      setDraft(prev => {
        const replaceAll = (text) => text ? text.split(tag).join(value) : "";
        
        const hasTag = (prev.subject && prev.subject.includes(tag)) || 
                       (prev.greeting && prev.greeting.includes(tag)) || 
                       (prev.body && prev.body.includes(tag)) || 
                       (prev.cta && prev.cta.includes(tag));
                       
        let newBody = prev.body || "";
        if (!hasTag) {
          newBody = newBody ? newBody + " " + value : value;
        } else {
          newBody = replaceAll(newBody);
        }

        return {
          subject: replaceAll(prev.subject),
          greeting: replaceAll(prev.greeting),
          body: newBody,
          cta: replaceAll(prev.cta)
        };
      });
    } else if (value === "") {
      // Just append the placeholder if they submit empty
      setDraft(prev => ({ ...prev, body: prev.body ? prev.body + " " + tag : tag }));
    }
  };

  const handleSendEmail = async () => {
    if (recipients.length === 0) {
      toast.error("Please select at least one recipient.");
      return;
    }

    const hasUnresolved = [draft.subject, draft.greeting, draft.body, draft.cta].some(
      text => text && text.match(/\{\{.*?\}\}/)
    );
    if (hasUnresolved) {
      toast.error("Please resolve all placeholders (e.g. {{CustomerName}}) before sending.");
      return;
    }
    
    setIsLoading(true);
    try {
      const data = {
        recipients,
        emailContent: draft
      };
      await emailAIService.sendBulkEmail(data);
      toast.success("Bulk emails sent successfully!");
      setTimeout(() => {
        setStep(1);
        setDraft({ subject: "", greeting: "", body: "", cta: "" });
        setRecipients([]);
      }, 2000);
    } catch (error) {
      toast.error("Failed to send emails.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="wrapper">
      <UserNavbar hamburger={hamburger} sethamburger={sethamburger} />
      <MainSidebar hamburger={hamburger} sethamburger={sethamburger} />
      
      <div 
        className="content-wrapper send-bulk-email-container"
        style={handleclickonbutton(hamburger)}
      >
        <ToastContainer position="top-right" autoClose={3000} />
        
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="m-0">AI-Powered Bulk Email Assistant</h3>
          <button 
            className="btn btn-outline-secondary"
            onClick={() => setShowHistory(true)}
          >
            <FontAwesomeIcon icon={faHistory} className="mr-2" /> Draft History
          </button>
        </div>

        {/* Stepper */}
        <div className="stepper">
          {/* Step 1 — always accessible when not on step 1 */}
          <div
            className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''} ${step !== 1 ? 'step-clickable' : ''}`}
            onClick={() => step !== 1 && setStep(1)}
            title={step !== 1 ? 'Go to Configure Intent' : ''}
          >
            <div className="step-circle">{step > 1 ? '✓' : '1'}</div>
            <div className="step-title">Configure Intent</div>
          </div>

          {/* Step 2 — accessible if draft has been generated (subject exists), from any step */}
          <div
            className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''} ${(draft.subject && step !== 2) ? 'step-clickable' : ''}`}
            onClick={() => draft.subject && step !== 2 && setStep(2)}
            title={draft.subject && step !== 2 ? 'Go to Edit Draft' : ''}
          >
            <div className="step-circle">{step > 2 ? '✓' : '2'}</div>
            <div className="step-title">Edit Draft</div>
          </div>

          {/* Step 3 — accessible if draft exists (can always select recipients after draft), from any step */}
          <div
            className={`step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''} ${(draft.subject && step !== 3) ? 'step-clickable' : ''}`}
            onClick={() => draft.subject && step !== 3 && setStep(3)}
            title={draft.subject && step !== 3 ? 'Go to Select Recipients' : ''}
          >
            <div className="step-circle">{step > 3 ? '✓' : '3'}</div>
            <div className="step-title">Select Recipients</div>
          </div>

          {/* Step 4 — accessible only if draft AND recipients are ready */}
          <div
            className={`step ${step >= 4 ? 'active' : ''} ${(draft.subject && recipients.length > 0 && step !== 4) ? 'step-clickable' : ''}`}
            onClick={() => draft.subject && recipients.length > 0 && step !== 4 && setStep(4)}
            title={draft.subject && recipients.length > 0 && step !== 4 ? 'Go to Preview & Send' : ''}
          >
            <div className="step-circle">4</div>
            <div className="step-title">Preview & Send</div>
          </div>
        </div>

        {/* Step 1: Configure Intent */}
        {step === 1 && (
          <div className="card">
            <div className="card-header">
              Configure Email Intent
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>Email Purpose <span className="text-danger">*</span></label>
                  <select className="form-select" name="purpose" value={aiConfig.purpose} onChange={handleConfigChange}>
                    <option value="">Select Purpose...</option>
                    {emailPurposes.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label>Tone</label>
                  <select className="form-select" name="tone" value={aiConfig.tone} onChange={handleConfigChange}>
                    <option>Professional</option>
                    <option>Friendly</option>
                    <option>Formal</option>
                    <option>Marketing</option>
                    <option>Sales</option>
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label>Product/Service Name <span className="text-danger">*</span></label>
                  <input type="text" className="form-control" name="productName" value={aiConfig.productName} onChange={handleConfigChange} placeholder="e.g. ENTELI-PRO CRM" />
                </div>
                <div className="col-md-6 mb-3">
                  <label>Target Audience</label>
                  <input type="text" className="form-control" name="audience" value={aiConfig.audience} onChange={handleConfigChange} placeholder="e.g. Existing Customers" />
                </div>
                <div className="col-md-12 mb-3">
                  <label>Keywords (comma separated)</label>
                  <input type="text" className="form-control" name="keywords" value={aiConfig.keywords} onChange={handleConfigChange} placeholder="e.g. discount, 20% off, upgrade" />
                </div>
                <div className="col-md-12 mb-4">
                  <label>Additional Instructions</label>
                  <textarea className="form-control" rows="2" name="instructions" value={aiConfig.instructions} onChange={handleConfigChange} placeholder="Any specific details AI should include..."></textarea>
                </div>
              </div>
              <div className="text-center">
                <button className="ai-generate-btn" onClick={handleGenerateAI} disabled={isLoading}>
                  {isLoading ? <FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> : <FontAwesomeIcon icon={faMagicWandSparkles} className="mr-2" />}
                  Generate with AI
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Edit Draft */}
        {step === 2 && (
          <div className="row">
            {/* Left Column: Email Editor */}
            <div className="col-md-7">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <span>Email Editor</span>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setStep(1)}>Back</button>
                </div>
                <div className="card-body">
                  <div className="placeholder-toolbar">
                    <strong className="d-block mb-2 text-muted" style={{fontSize: '0.8rem'}}>Dynamic Placeholders:</strong>
                    {placeholders.map(p => (
                      <span key={p} className="placeholder-btn" onClick={() => insertPlaceholder(p)}>
                        {p}
                      </span>
                    ))}
                  </div>
                  
                  <div className="mb-3">
                    <label>Subject Line</label>
                    <input type="text" className="form-control font-weight-bold" name="subject" value={draft.subject} onChange={handleDraftChange} />
                  </div>
                  <div className="mb-3">
                    <label>Greeting</label>
                    <input type="text" className="form-control" name="greeting" value={draft.greeting} onChange={handleDraftChange} />
                  </div>
                  <div className="mb-3">
                    <label>Email Body</label>
                    <textarea className="form-control" rows="8" name="body" value={draft.body} onChange={handleDraftChange}></textarea>
                  </div>
                  <div className="mb-3">
                    <label>Call to Action</label>
                    <input type="text" className="form-control" name="cta" value={draft.cta} onChange={handleDraftChange} />
                  </div>
                  
                  <div className="d-flex justify-content-end mt-4">
                    <button className="btn btn-primary" onClick={() => setStep(3)}>Continue to Recipients</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Live Email Preview & Smart Suggestions */}
            <div className="col-md-5">
              {/* Live Email Preview */}
              <div className="card mb-3">
                <div className="card-header">
                  <span>📧 Email Preview</span>
                </div>
                <div className="card-body p-0">
                  <div className="email-preview-wrapper">
                    {/* Email header bar */}
                    <div className="email-preview-header">
                      <div className="email-preview-header-row">
                        <span className="email-preview-label">From:</span>
                        <span>ENTELI-PRO Team &lt;noreply@enteli-pro.com&gt;</span>
                      </div>
                      <div className="email-preview-header-row">
                        <span className="email-preview-label">To:</span>
                        <span>{'{{CustomerName}}'} &lt;recipient@example.com&gt;</span>
                      </div>
                      <div className="email-preview-header-row">
                        <span className="email-preview-label">Subject:</span>
                        <strong>{draft.subject || '(No subject)'}</strong>
                      </div>
                    </div>
                    {/* Email body */}
                    <div className="email-preview-body">
                      <p className="email-preview-greeting">{draft.greeting || 'Hello,'}</p>
                      <div className="email-preview-text" style={{whiteSpace: 'pre-wrap'}}>
                        {draft.body || '(Email body will appear here)'}
                      </div>
                      {draft.cta && (
                        <div className="email-preview-cta-wrapper">
                          <span className="email-preview-cta-button">{draft.cta}</span>
                        </div>
                      )}
                      <div className="email-preview-footer-text">
                        <p>Best regards,<br/>The ENTELI-PRO Team</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Smart Suggestions */}
              <div className="card">
                <div className="card-header">
                  <FontAwesomeIcon icon={faWandMagicSparkles} className="mr-2" /> Smart Suggestions
                </div>
                <div className="card-body">
                  <div className="suggestion-chip" onClick={() => applySuggestion("Improve subject line")}>
                    <i className="fa fa-pen"></i> Improve subject line
                  </div>
                  <div className="suggestion-chip" onClick={() => applySuggestion("Make email shorter")}>
                    <i className="fa fa-compress-alt"></i> Make email shorter
                  </div>
                  <div className="suggestion-chip" onClick={() => applySuggestion("Make email more persuasive")}>
                    <i className="fa fa-bullhorn"></i> Make email more persuasive
                  </div>
                  <div className="suggestion-chip" onClick={() => applySuggestion("Rewrite professionally")}>
                    <i className="fa fa-user-tie"></i> Rewrite professionally
                  </div>
                  <div className="suggestion-chip" onClick={() => applySuggestion("Fix grammar")}>
                    <i className="fa fa-spell-check"></i> Fix grammar
                  </div>
                  <div className="suggestion-chip" onClick={() => applySuggestion("Increase engagement")}>
                    <i className="fa fa-chart-line"></i> Increase engagement
                  </div>

                  {isLoading && (
                    <div className="text-center mt-3">
                      <FontAwesomeIcon icon={faSpinner} spin className="text-primary" size="lg" />
                      <p className="small text-muted mt-2">AI is processing...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Select Recipients */}
        {step === 3 && (
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span>Select Recipients</span>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setStep(2)}>Back</button>
            </div>
            <div className="card-body">
              <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                  <a className={`nav-link ${recipientTab === 'crm' ? 'active' : ''}`} href="#!" onClick={() => setRecipientTab('crm')}>
                    <FontAwesomeIcon icon={faUsers} className="mr-2"/> From CRM
                  </a>
                </li>
                <li className="nav-item">
                  <a className={`nav-link ${recipientTab === 'csv' ? 'active' : ''}`} href="#!" onClick={() => setRecipientTab('csv')}>
                    <FontAwesomeIcon icon={faFileCsv} className="mr-2"/> Upload CSV
                  </a>
                </li>
              </ul>

              {recipientTab === 'crm' && (
                <div>
                  <div className="alert alert-info py-2">
                    <small>For demonstration, selecting a group adds mock recipients.</small>
                  </div>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <button className="btn btn-outline-primary w-100" onClick={() => setRecipients([{name: 'John Doe', email: 'john@example.com'}, {name: 'Jane Smith', email: 'jane@example.com'}])}>
                        Select All Customers
                      </button>
                    </div>
                    <div className="col-md-4 mb-3">
                      <button className="btn btn-outline-primary w-100" onClick={() => setRecipients([{name: 'Lead 1', email: 'lead1@example.com'}])}>
                        Select All Leads
                      </button>
                    </div>
                    <div className="col-md-4 mb-3">
                      <button className="btn btn-outline-primary w-100" onClick={() => setRecipients([{name: 'Vendor 1', email: 'vendor1@example.com'}])}>
                        Select All Vendors
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {recipientTab === 'csv' && (
                <div className="text-center p-5 border border-dashed rounded bg-light">
                  <FontAwesomeIcon icon={faFileCsv} size="3x" className="text-secondary mb-3" />
                  <h5>Upload CSV File</h5>
                  <p className="text-muted small">File should contain 'Name' and 'Email' columns.</p>
                  <input type="file" className="form-control w-50 mx-auto" />
                  <button className="btn btn-secondary mt-3" onClick={() => setRecipients([{name: 'CSV User', email: 'csv@example.com'}])}>
                    Simulate Upload
                  </button>
                </div>
              )}

              <div className="mt-4">
                <h6>Selected Recipients: <span className="badge badge-success">{recipients.length}</span></h6>
                {recipients.length > 0 && (
                  <table className="table table-sm recipient-table mt-2">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipients.map((r, i) => (
                        <tr key={i}>
                          <td>{r.name}</td>
                          <td>{r.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="d-flex justify-content-end mt-4">
                <button className="btn btn-primary" onClick={() => setStep(4)} disabled={recipients.length === 0}>
                  Continue to Preview
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Preview & Send */}
        {step === 4 && (
          <div className="row">
            <div className="col-md-8 mx-auto">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <span>Preview & Send</span>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setStep(3)}>Back</button>
                </div>
                <div className="card-body">
                  <div className="alert alert-warning py-2 mb-4">
                    <small>
                      <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                      You are about to send this email to <strong>{recipients.length}</strong> recipients.
                    </small>
                  </div>

                  <div className="border rounded p-4 bg-white mb-4 shadow-sm">
                    <div className="border-bottom pb-2 mb-3">
                      <strong>Subject:</strong> {draft.subject}
                    </div>
                    <div className="email-preview-content" style={{whiteSpace: 'pre-wrap'}}>
                      {draft.greeting + '\n\n'}
                      {draft.body + '\n\n'}
                      {draft.cta && (
                        <div className="mt-3">
                          <button className="btn btn-sm btn-primary">{draft.cta}</button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="text-center">
                    <button className="btn btn-success btn-lg px-5" onClick={handleSendEmail} disabled={isLoading}>
                      {isLoading ? (
                        <><FontAwesomeIcon icon={faSpinner} spin className="mr-2" /> Sending...</>
                      ) : (
                        <><FontAwesomeIcon icon={faPaperPlane} className="mr-2" /> Send Bulk Email</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <EmailDraftHistory 
        isOpen={showHistory} 
        onClose={() => setShowHistory(false)} 
        onSelectDraft={(d) => {
          setDraft({
            subject: d.subject || "",
            greeting: d.greeting || "",
            body: d.body || "",
            cta: d.cta || ""
          });
          setAiConfig(prev => ({ ...prev, purpose: d.purpose || prev.purpose }));
          setStep(2);
        }}
      />
      
      <UserFooter />
    </div>
  );
}
